import { useUserDirectory } from "@/hooks/use-user-directory";
import { AsyncSelectOptions } from "@/components/ui/FormFields/AsyncSelectOptions";
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from "@/components/ui";
import type { MatterRole, DirectoryUser, User } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  type DeterministicColor,
  getDeterministicColorFromId,
} from "@/lib/utils/deterministic-color";
import { formatUsersName } from "@/lib/utils/format";
import { useEffect, useState } from "react";

const getUserInitials = (user?: Pick<User, "firstName" | "lastName">) =>
  [user?.firstName, user?.lastName]
    .map((value) => value?.trim().charAt(0))
    .filter(Boolean)
    .join("")
    .toUpperCase() || "?";

const userInitialsColorClassNames: Record<DeterministicColor, string> = {
  blue: "bg-sky-500",
  green: "bg-teal-500",
  orange: "bg-amber-500",
  purple: "bg-violet-500",
  red: "bg-rose-500",
  yellow: "bg-yellow-500",
};

type LeadAssignPopoverProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  disabled?: boolean;
  matterRoles: MatterRole[];
  isAssigning?: boolean;
  /** When the matter already has assignees, the trigger reads "Assign more" instead of "Assign to...". */
  hasAssignees?: boolean;
  onAssign: (payload: { user: DirectoryUser; id: string }) => void;
};

export function LeadAssignPopover({
  open,
  setOpen,
  disabled,
  matterRoles,
  isAssigning,
  hasAssignees = false,
  onAssign,
}: LeadAssignPopoverProps) {
  const defaultMatterRole = matterRoles.at(0);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [roleSelectOpen, setRoleSelectOpen] = useState(false);
  const userDirectory = useUserDirectory({
    enabled: open,
    open,
    selectedIds: [selectedUserId],
  });
  const {
    users,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = userDirectory;
  const selectedUser = users.find((user) => user.id === selectedUserId);

  useEffect(() => {
    if (!open) {
      setSelectedUserId("");
      setSelectedRoleId("");
      setRoleSelectOpen(false);
      return;
    }

    setSelectedRoleId((current) => current || defaultMatterRole?.id || "");
  }, [defaultMatterRole?.id, open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="link"
          className="px-0"
          disabled={disabled}
        >
          {hasAssignees ? "Assign more" : "Assign to..."}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="w-[340px] space-y-3 rounded-lg p-3"
        onInteractOutside={(event) => {
          if (roleSelectOpen) {
            event.preventDefault();
          }
        }}
      >
        <div>
          <Typography
            weight="medium"
            size="xs"
            className="text-muted-foreground mb-2 leading-4"
          >
            Select assignee
          </Typography>
          <Command className="rounded-md border" shouldFilter={false}>
            <CommandInput
              value={userDirectory.search}
              onValueChange={userDirectory.setSearch}
              placeholder="Search agents..."
              className="h-8 text-sm"
            />
            <CommandList className="max-h-[130px]">
              {isUsersLoading ? (
                <CommandEmpty>Loading agents...</CommandEmpty>
              ) : null}
              {isUsersError ? (
                <CommandEmpty>Unable to load agents.</CommandEmpty>
              ) : null}
              {!isUsersLoading && !isUsersError ? (
                <CommandEmpty>No agents found.</CommandEmpty>
              ) : null}
              {users
                .filter(
                  (user) =>
                    !userDirectory.search.trim() ||
                    userDirectory.selectProps.matchingValues.includes(
                      user.id,
                    ) ||
                    `${formatUsersName(user)} ${user.email}`
                      .toLowerCase()
                      .includes(userDirectory.search.trim().toLowerCase()),
                )
                .map((user) => (
                  <CommandItem
                    key={user.id}
                    value={`${formatUsersName(user)} ${user.email}`}
                    className={cn(
                      "cursor-pointer gap-2 px-3 py-2",
                      selectedUserId === user.id &&
                        "bg-muted hover:bg-muted data-[selected=true]:bg-muted",
                    )}
                    onSelect={() => {
                      setSelectedUserId(user.id);
                    }}
                  >
                    <div
                      className={cn(
                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] leading-5 font-normal text-white",
                        userInitialsColorClassNames[
                          getDeterministicColorFromId(user.id)
                        ],
                      )}
                    >
                      {getUserInitials(user)}
                    </div>
                    <Typography className="truncate font-normal">
                      {formatUsersName(user)}
                    </Typography>
                    {selectedUserId === user.id ? (
                      <Typography className="text-foreground ml-auto shrink-0 font-normal">
                        Selected
                      </Typography>
                    ) : null}
                  </CommandItem>
                ))}
              <AsyncSelectOptions {...userDirectory.selectProps} />
            </CommandList>
          </Command>
        </div>

        <div>
          <Typography
            weight="medium"
            size="xs"
            className="text-muted-foreground mb-2 leading-4"
          >
            Matter role
          </Typography>
          <Select
            open={roleSelectOpen}
            onOpenChange={setRoleSelectOpen}
            value={selectedRoleId}
            onValueChange={setSelectedRoleId}
          >
            <SelectTrigger className="bg-background h-8 text-sm">
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {matterRoles.map((role) => (
                <SelectItem key={role.id} value={role.id}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!selectedUser || !selectedRoleId || isAssigning}
            onClick={() => {
              if (!selectedUser || !selectedRoleId) {
                return;
              }

              onAssign({ user: selectedUser, id: selectedRoleId });
            }}
          >
            {isAssigning ? "Assigning..." : "Assign"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
